<template>
  <v-container class="pa-0">
    <v-row  v-if="formattedItems.length > 5 && !fetchJobsPending">
      <v-col :cols="12" :md="3" :offset-md="9">
        <v-text-field
          append-icon="search"
          :label="$t('texts.search')"
          single-line
          hide-details
          v-model="search"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col :cols="12">
        <v-data-table
          v-model="selected"
          v-bind:headers="filteredHeaders"
          :items="formattedItems"
          v-bind:search="search"
          item-key="id"
          must-sort
          :loading="fetchJobsPending"
          :hide-default-footer="formattedItems.length < 5 || fetchJobsPending"
        >
          <template v-slot:item.formattedStatusColor="{ item }">
            <v-tooltip
              :color="item.formattedStatusColor" top
              :disabled="!item.isJobroomEntry || item.jobroom_entry.status !== 'REJECTED'"
              :max-width="200"
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  v-on="on"
                  label
                  :color="item.formattedStatusColor"
                  style="width: 120px"
                >
                  {{ item.formattedStatusText }}
                </v-chip>
              </template>
              <span v-if="item.jobroom_entry && item.jobroom_entry.status === 'REJECTED'">
                {{ item.jobroom_entry.rejection_reason }}
              </span>
            </v-tooltip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip color="info" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  icon
                  :to="{
                    name: 'company.jobs.edit',
                    params: {
                      id: item.id,
                      job: item,
                      company,
                      type: 'edit',
                    }
                  }"
                >
                  <v-icon color="primary">
                    {{ item.isJobroomEntry ? 'remove_red_eye' : 'mode_edit'}}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ $t('texts.edit') }}</span>
            </v-tooltip>
            <v-tooltip color="info" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  icon
                  :to="{
                    name: 'company.jobs.create',
                    params: {
                      job: item,
                      company,
                      type: 'copy',
                    }
                  }"
                >
                  <v-icon color="primary">file_copy</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('texts.copy') }}</span>
            </v-tooltip>
            <v-tooltip color="info" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  icon
                  v-on:click.stop="showDeleteJobDialog(item)"
                  :disabled="item.isJobroomEntry && item.jobroom_entry.status === 'INSPECTING'"
                >
                  <v-icon color="primary">delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('texts.delete') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import moment from 'moment';

const { mapState: mapCompanyState } = createNamespacedHelpers('company');
const { mapState: mapCompanyJobsState, mapActions: mapCompanyJobsActions } = createNamespacedHelpers('company/jobs');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'JobsDatatable',
  data() {
    return {
      fulltime: true,
      search: '',
      pagination: {},
      selected: [],
      jobUpdateId: null,
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  created() {
    // inspecting, active, draft, trashed
    this.fetchJobs({
      companyId: this.selectedCompanyId,
      listType: this.listType,
    });
  },
  computed: {
    ...mapCompanyState([
      'company',
    ]),
    ...mapCompanyJobsState([
      'fetchJobsPending',
      'storeJobPending',
      'jobs',
    ]),
    headers() {
      const headers = [
        {
          text: this.$i18n.t('texts.status'),
          value: 'formattedStatusColor',
          list: [
            'activeWithRestricted',
          ],
        },
        {
          text: this.$i18n.t('texts.label'),
          value: 'title',
          align: 'left',
          sortable: true,
          list: [
            'activeWithRestricted',
            'draft',
            'trashed',
          ],
        },
        {
          text: `${this.$i18n.t('texts.percent')} %`,
          value: 'formattedJobWorkload',
          sortable: true,
          align: 'right',
          list: [
            'activeWithRestricted',
            'draft',
            'trashed',
          ],
        },
        {
          text: this.$i18n.t('texts.start'),
          value: 'formattedStartMonth',
          sortable: true,
          align: 'left',
          list: [
            'activeWithRestricted',
            'draft',
            'trashed',
          ],
        },
        {
          text: this.$i18n.t('texts.end'),
          value: 'formattedEndMonth',
          sortable: true,
          align: 'left',
          list: [
            'activeWithRestricted',
            'draft',
            'trashed',
          ],
        },
        {
          text: this.$i18n.t('texts.applications'),
          value: 'number_of_applications',
          sortable: true,
          align: 'left',
          list: [
            'activeWithRestricted',
            'draft',
            'trashed',
          ],
        },
        {
          text: this.$i18n.t('texts.createdOn'),
          value: 'formattedCreatedAt',
          sortable: true,
          align: 'left',
          list: [
            'activeWithRestricted',
            'trashed',
          ],
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'right',
          list: [
            'activeWithRestricted',
            'draft',
            'trashed',
          ],
        },
      ];
      return headers;
    },
    filteredHeaders() {
      if (this.listType) {
        return this.headers.filter((item) => item.list.includes(this.listType));
      }
      return this.headers;
    },
    selectedCompanyId() {
      return this.$route.params.companyId;
    },
    listType() {
      return this.$route.meta.listType;
    },
    formattedItems() {
      if (this.jobs[this.listType].length > 0) {
        return this.jobs[this.listType].map((item) => {
          const isJobroomEntry = !!item.jobroom_entry;
          let formattedStatusColor = 'success';
          let formattedStatusText = this.$i18n.t('texts.jobStatus.PUBLISHED_PUBLIC');
          let daysToPublish = '';
          if (isJobroomEntry) {
            formattedStatusText = this.$i18n.t(`texts.jobStatus.${item.jobroom_entry.status}`);
            switch (item.jobroom_entry.status) {
              case 'INSPECTING':
                formattedStatusColor = 'info';
                break;
              case 'PUBLISHED_RESTRICTED':
                formattedStatusColor = 'warning';
                daysToPublish = moment(item.jobroom_entry.reporting_obligation_end_date).diff(moment().format('YYYY-MM-DD'), 'days');
                formattedStatusText = `${formattedStatusText} (${daysToPublish})`;
                break;
              case 'ARCHIVED':
                formattedStatusText = this.$i18n.t('texts.jobStatus.PUBLISHED_PUBLIC');
                break;
              case 'REJECTED':
                formattedStatusColor = 'error';
                break;
              default:
                formattedStatusColor = 'success';
            }
          }
          const formattedJobWorkload = this.$options.filters.formatJobWorkload(item);
          const formattedStartMonth = this.$i18n.t(`texts.months.${item.start_month}`);
          const formattedEndMonth = this.$i18n.t(`texts.months.${item.end_month}`);
          const formattedCreatedAt = this.$options.filters.formatDateShort(item.created_at);
          const formattedItem = {
            ...item,
            formattedStatusColor,
            formattedStatusText,
            formattedJobWorkload,
            formattedStartMonth,
            formattedEndMonth,
            formattedCreatedAt,
            isJobroomEntry,
          };
          return formattedItem;
        });
      }
      return [];
    },
  },
  methods: {
    ...mapCompanyJobsActions([
      'fetchJobs',
      'storeJob',
    ]),
    setListHeader() {
      this.headers = Headers.payments.filter((column) => column.listType.includes(this.listType));
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    showDeleteJobDialog(item) {
      this.$eventBus.$emit('show-delete-job-dialog', {
        id: item.id,
        title: item.title,
        isObligationRequirement: !!item.jobroom_entry,
        listType: this.listType,
      });
    },
    triggerVisibility(job, index) {
      const newJob = { ...job };
      newJob.status = newJob.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      this.jobUpdateId = index;
      this.storeJob({
        companyId: this.selectedCompanyId,
        form: newJob,
      }).then(() => {
        this.jobUpdateId = null;
      }).catch((error) => {
        this.jobUpdateId = null;
      });
    },
  },
};
</script>
